/**
 * TanStack Query hooks for the job detail screen.
 *
 * Backend (apps/api/flow_api/routers):
 *   GET  /jobs/{id}              jobs.py       viewer and above
 *   GET  /jobs/{id}/steps        jobs.py       supervisor and above
 *   GET  /approvals?job_id=      approvals.py  operator and above
 *
 * Stop / cancel, live view and takeover are in `liveApi.ts`.
 *
 * Polling: every 5s while the job is not finished, then it stops. Each
 * route allows 120 requests a minute per user, so three polled routes
 * at 12 a minute leave room for the dashboard's own 2s poll in another
 * tab. A 403 or 404 also stops the poll: retrying cannot change it.
 */
import { useQuery } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/lib/apiClient";
import { toUiJobStatus } from "@/lib/jobStatus";
import type { JobStatus } from "@/lib/mockData";

import type { ApprovalItem } from "@/lib/hooks/useApprovalsApi";

import type { JobStepsPayload } from "./replayApi";

export const JOB_POLL_MS = 5_000;

export interface PhaseDetail {
  id: string;
  order: number;
  name: string;
  riskLevel: string;
  /** Step rows recorded for this phase on this job (phase-start markers excluded). */
  stepCount: number;
  /** The runner started this phase (a `phase_started` row). False on older API builds. */
  started: boolean;
  /** A `phase_failed` step was recorded. */
  failed: boolean;
  lastStepAt: string | null;
}

export interface JobDetail {
  id: string;
  workflow: { id: string; name: string; portal: string };
  entity: { id: string; name: string; full: string };
  user: { id: string; name: string; initials: string; role: string } | null;
  /** UI vocabulary, translated from the API status in the hook. */
  status: JobStatus;
  /** The status string exactly as the API sent it. */
  apiStatus: string;
  phases: string[];
  phaseDetails: PhaseDetail[];
  /**
   * `order` of the phase the runner is in, or stopped in. Authoritative
   * even while that phase records no steps (waiting for a person, at a
   * checkpoint, under a supervisor's control). Null when unknown: not
   * started, a smoke run, a run from before the marker existed, or an
   * older API build.
   */
  currentPhaseOrder: number | null;
  elapsed: number;
  queuedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  /** `jobs.failure_reason`. Empty when there is none. */
  resultSummary: string;
  workflowVersion: number | null;
  runMode: string | null;
  testMode: boolean | null;
}

type ApiPhaseDetail = Omit<PhaseDetail, "started"> & { started?: boolean };

type ApiJobDetail = Omit<
  JobDetail,
  "status" | "apiStatus" | "phaseDetails" | "currentPhaseOrder"
> & {
  status: string;
  phaseDetails?: ApiPhaseDetail[];
  currentPhaseOrder?: number | null;
};

export function normalizeJobDetail(raw: ApiJobDetail): JobDetail {
  return {
    ...raw,
    status: toUiJobStatus(raw.status),
    apiStatus: String(raw.status),
    phaseDetails: (raw.phaseDetails ?? []).map((p) => ({ ...p, started: p.started ?? false })),
    currentPhaseOrder:
      typeof raw.currentPhaseOrder === "number" ? raw.currentPhaseOrder : null,
    queuedAt: raw.queuedAt ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
    workflowVersion: raw.workflowVersion ?? null,
    runMode: raw.runMode ?? null,
    testMode: raw.testMode ?? null,
  };
}

/** No further change is coming: stop polling, hide live controls. */
export function isFinished(status: JobStatus): boolean {
  return status === "success" || status === "failed" || status === "human";
}

function isFinal(error: unknown): boolean {
  return error instanceof ApiError && error.status >= 400 && error.status < 500;
}

export function useJobDetail(jobId: string | undefined) {
  return useQuery<JobDetail>({
    queryKey: ["jobs", jobId, "detail"],
    queryFn: async () => normalizeJobDetail(await apiFetch<ApiJobDetail>(`/jobs/${jobId}`)),
    enabled: Boolean(jobId),
    staleTime: 2_000,
    retry: (count, error) => !isFinal(error) && count < 2,
    refetchInterval: (query) => {
      if (isFinal(query.state.error)) return false;
      const job = query.state.data;
      return job && isFinished(job.status) ? false : JOB_POLL_MS;
    },
  });
}

export function useLiveJobSteps(jobId: string | undefined, live: boolean) {
  return useQuery<JobStepsPayload>({
    queryKey: ["jobs", jobId, "steps", "live"],
    queryFn: () => apiFetch(`/jobs/${jobId}/steps`),
    enabled: Boolean(jobId),
    staleTime: 2_000,
    retry: (count, error) => !isFinal(error) && count < 2,
    refetchInterval: (query) => (live && !isFinal(query.state.error) ? JOB_POLL_MS : false),
  });
}

export type PendingApproval = ApprovalItem;

export function useJobApprovals(jobId: string | undefined, live: boolean) {
  return useQuery<{ items: PendingApproval[]; total: number }>({
    queryKey: ["approvals", "job", jobId],
    queryFn: () => apiFetch(`/approvals?job_id=${jobId}`),
    // A finished job cannot have a decision waiting on it.
    enabled: Boolean(jobId) && live,
    staleTime: 2_000,
    retry: (count, error) => !isFinal(error) && count < 2,
    refetchInterval: (query) => (live && !isFinal(query.state.error) ? JOB_POLL_MS : false),
  });
}
